import * as React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    EditIcon,
    H5Emphasized,
    InfoRow,
    isNonMobileWeb,
    RowList,
    View,
    YStack,
} from 'smoothie';

import { preferLegalName } from '../../../../common/utils/bankingUtils';
import { type User } from '../../../../user/types/User';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

interface ContactInfoProps {
    user: User;
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
    onPressEdit: () => void;
    kycData: Partial<RunKYCVerifyBody>;
}

const ContactInfo = ({
    user,
    kycData,
    onPressNext,
    onPressEdit,
}: ContactInfoProps) => {
    const [firstName, lastName] = preferLegalName(user);

    const onConfirm = () => {
        onPressNext({
            firstName: kycData.firstName ?? firstName,
            lastName: kycData.lastName ?? lastName,
        });
    };

    return (
        <YStack
            flex={1}
            justifyContent="space-between"
            gap={isNonMobileWeb ? 60 : 16}
        >
            <View>
                <H5Emphasized marginBottom={8}>
                    Is this information correct?
                </H5Emphasized>
                <BodyMediumRegular color="$gray1000" marginBottom={24}>
                    Please confirm that the details you entered during sign-up
                    match what&apos;s on your ID.
                </BodyMediumRegular>
                {/* for these fields, prefer user provided data when navigating back */}
                <RowList>
                    <InfoRow title="First name" value={firstName} />
                    <InfoRow title="Last name" value={lastName} />
                </RowList>
                <BodySmallRegular color="$gray1000" marginTop={12}>
                    This information will be used to verify your identity. Any
                    changes you make here will not be reflected in your
                    membership profile.
                </BodySmallRegular>
            </View>
            <YStack gap={8}>
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    text="Edit my information"
                    icon={EditIcon}
                    onPress={onPressEdit}
                />
                <KycButton
                    text="Looks good to me"
                    onPress={onConfirm}
                    hideSecureText
                />
            </YStack>
        </YStack>
    );
};

export default ContactInfo;
